// The picture of an angle, or of several angles: round a point, on a
// straight line, where two lines cross, or in the corners of a shape. Plain
// React SVG, like practice-graphs.tsx.
//
// Every angle is drawn its true size. An angle marked 90° that is drawn
// square carries a small square in place of an arc.

import {
  ANGLE_LABEL_SIZE,
  anglePicture,
  describeAngle,
  describeAnglesAtPoint,
  describeTransversal,
  type AnglePoint,
  type AngleSpec,
  type AnglesAtPointSpec,
  type TransversalSpec,
} from './practice-angle-data';

const INK = '#2d4a3e';
const PROTRACTOR_FILL = '#eef3ee';

const fixed = (value: number) => value.toFixed(2);
const at = (point: AnglePoint) => `${fixed(point.x)} ${fixed(point.y)}`;

// A half circle with a mark at every degree and a longer one at every 5 and
// 10. It is read anticlockwise from the right, the way the angle opens.
function protractorPoint(degrees: number, reach: number): AnglePoint {
  const turn = (degrees * Math.PI) / 180;
  return { x: reach * Math.cos(turn), y: -reach * Math.sin(turn) };
}

function Protractor({ radius }: { radius: number }) {
  const ticks = (every: number, length: number) =>
    Array.from({ length: 180 / every + 1 }, (_, index) => index * every)
      .map(
        (degrees) =>
          `M ${at(protractorPoint(degrees, radius))} L ${at(protractorPoint(degrees, radius - length))}`,
      )
      .join(' ');
  return (
    <g data-protractor="scale">
      <path
        d={`M ${fixed(radius)} 0 A ${fixed(radius)} ${fixed(radius)} 0 0 0 ${fixed(-radius)} 0 Z`}
        fill={PROTRACTOR_FILL}
        stroke={INK}
        strokeWidth="1.2"
      />
      <path d={ticks(1, 5)} stroke={INK} strokeWidth="0.6" />
      <path d={ticks(5, 9)} stroke={INK} strokeWidth="0.9" />
      <path d={ticks(10, 13)} stroke={INK} strokeWidth="1.2" />
    </g>
  );
}

// The number at every 10 degrees, on two scales as on a real protractor: the
// outer one counts up from the right (where the angle's first arm lies), the
// inner one counts up from the left. Reading the wrong scale is the mistake
// Year 5 protractor questions are about (14 Oct 2026). Drawn over the lines
// of the angle, each with a rim of the protractor's own colour, so a line
// never hides one.
function ProtractorNumbers({ radius }: { radius: number }) {
  const ring = (reach: number, size: string, weight: string, inner: boolean) =>
    Array.from({ length: 19 }, (_, index) => index * 10).map((degrees) => {
      const place = protractorPoint(degrees, reach);
      return (
        <text
          key={degrees}
          x={fixed(place.x)}
          // 0 and 180 sit just above the bottom edge.
          y={fixed(Math.min(place.y, -7))}
          dy="0.35em"
          textAnchor="middle"
          fill={INK}
          fontSize={size}
          fontWeight={weight}
          stroke={PROTRACTOR_FILL}
          strokeWidth="3"
          paintOrder="stroke"
        >
          {inner ? 180 - degrees : degrees}
        </text>
      );
    });
  return (
    <g data-protractor="numbers">
      <g data-scale="outer">{ring(radius - 22, '9', '600', false)}</g>
      <g data-scale="inner">{ring(radius - 36, '7.5', '500', true)}</g>
    </g>
  );
}

function describe(angle: AngleSpec | AnglesAtPointSpec | TransversalSpec) {
  if (angle.type === 'angle') return describeAngle(angle);
  if (angle.type === 'transversal') return describeTransversal(angle);
  return describeAnglesAtPoint(angle);
}

export function AnglePictureView({
  angle,
}: {
  angle: AngleSpec | AnglesAtPointSpec | TransversalSpec;
}) {
  const picture = anglePicture(angle);
  const { box } = picture;

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describe(angle)}
        viewBox={`${fixed(box.x)} ${fixed(box.y)} ${fixed(box.width)} ${fixed(box.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(box.width * 1.4))}px` }}
      >
        {picture.protractor !== null && (
          <Protractor radius={picture.protractor} />
        )}
        {picture.marks.map((mark, index) => {
          const [first, ...rest] = mark.points;
          const last = rest[rest.length - 1];
          if (!first || !last) return null;
          return (
            <path
              key={index}
              data-mark={mark.square ? 'square' : 'arc'}
              d={
                mark.square
                  ? `M ${at(first)} ${rest.map((point) => `L ${at(point)}`).join(' ')}`
                  : `M ${at(first)} A ${fixed(mark.radius)} ${fixed(mark.radius)} 0 ${mark.large ? '1' : '0'} 1 ${at(last)}`
              }
              fill="none"
              stroke={INK}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          );
        })}
        {picture.lines.map((line, index) => (
          <line
            key={index}
            data-ray="true"
            x1={fixed(line.from.x)}
            y1={fixed(line.from.y)}
            x2={fixed(line.to.x)}
            y2={fixed(line.to.y)}
            stroke={INK}
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        ))}
        {picture.vertex && (
          <circle
            cx={fixed(picture.vertex.x)}
            cy={fixed(picture.vertex.y)}
            r="3"
            fill={INK}
          />
        )}
        {picture.protractor !== null && (
          <ProtractorNumbers radius={picture.protractor} />
        )}
        {picture.labels.map((label, index) => (
          <text
            key={index}
            data-label="true"
            x={fixed(label.x)}
            y={fixed(label.y)}
            dy="0.35em"
            textAnchor="middle"
            fill={INK}
            fontSize={ANGLE_LABEL_SIZE}
            fontWeight="700"
          >
            {label.text}
          </text>
        ))}
      </svg>
    </figure>
  );
}
